import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/Bits";
import { toast } from "sonner";

const TABS = ["Overview", "Users", "Pricing", "Signals", "Feature Flags", "Audit"];
const ROLES = ["user", "pro_user", "premium_user", "admin", "super_admin"];

export default function Admin() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [flags, setFlags] = useState({});
  const [logs, setLogs] = useState([]);
  const [sigs, setSigs] = useState(null);

  useEffect(() => {
    if (!user || !["admin", "super_admin"].includes(user.role)) return;
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/pricing").then((r) => setPricing(r.data.plans)).catch(() => {});
    api.get("/admin/feature-flags").then((r) => setFlags(r.data.flags)).catch(() => {});
    api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs)).catch(() => {});
    api.get("/admin/signals").then((r) => setSigs(r.data.analytics)).catch(() => {});
  }, [user]);

  if (checked && (!user || !["admin", "super_admin"].includes(user.role))) return <Navigate to="/app" replace />;

  const setRole = async (id, role) => { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); setUsers((u) => u.map((x) => x.id === id ? { ...x, role } : x)); };
  const savePricing = async () => { await api.put("/admin/pricing", { plans: pricing }); toast.success("Pricing saved"); };
  const saveFlags = async (k, v) => { const nf = { ...flags, [k]: v }; setFlags(nf); await api.put("/admin/feature-flags", { flags: nf }); };
  const updatePrice = (idx, field, val) => setPricing((p) => p.map((pl, i) => i === idx ? { ...pl, [field]: +val } : pl));

  return (
    <DashboardLayout title="Admin Panel">
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(/\s/g, "-").toLowerCase()}`} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{t}</button>)}
      </div>

      {tab === "Overview" && metrics && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Users" value={metrics.users} testid="admin-users" />
          <StatCard label="Paid Users" value={metrics.paid_users} accent="text-bullish" />
          <StatCard label="MRR" value={`€${fmt(metrics.mrr, 0)}`} testid="admin-mrr" />
          <StatCard label="ARR" value={`€${fmt(metrics.arr, 0)}`} />
          <StatCard label="Conversion" value={`${metrics.conversion}%`} />
          <StatCard label="Churn" value={`${metrics.churn}%`} accent="text-warning" />
          <StatCard label="Referrals" value={metrics.referrals} />
          <StatCard label="AI Requests" value={metrics.ai_requests} />
          <StatCard label="Verified Users" value={metrics.verified_users} />
          <StatCard label="Signal Win Rate" value={`${metrics.signals.win_rate}%`} />
          <StatCard label="Market Data" value={metrics.market_data_status} accent="text-warning" />
          <StatCard label="Errors" value={metrics.errors} accent="text-bullish" />
        </div>
      )}

      {tab === "Users" && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground"><th className="px-4 py-3">Email</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Role</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 uppercase text-xs">{u.plan}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} data-testid={`admin-role-${u.email}`} className="rounded-lg border border-border bg-card px-2 py-1 text-xs">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Pricing" && (
        <div>
          <div className="grid gap-4 md:grid-cols-2">
            {pricing.filter((p) => p.id !== "free").map((p, i) => {
              const idx = pricing.findIndex((x) => x.id === p.id);
              return (
                <div key={p.id} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <label className="block">Monthly (€)<input type="number" value={p.price_monthly} onChange={(e) => updatePrice(idx, "price_monthly", e.target.value)} data-testid={`admin-price-${p.id}-monthly`} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 font-mono" /></label>
                    <label className="block">Yearly (€)<input type="number" value={p.price_yearly} onChange={(e) => updatePrice(idx, "price_yearly", e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 font-mono" /></label>
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={savePricing} data-testid="admin-save-pricing" className="mt-4 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Save Pricing</button>
          <p className="mt-2 text-xs text-muted-foreground">Note: display prices update instantly. Stripe checkout uses the configured catalog prices.</p>
        </div>
      )}

      {tab === "Signals" && sigs && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Total" value={sigs.total_signals} />
          <StatCard label="Wins" value={sigs.wins} accent="text-bullish" />
          <StatCard label="Losses" value={sigs.losses} accent="text-bearish" />
          <StatCard label="Win Rate" value={`${sigs.win_rate}%`} />
          <StatCard label="Avg R/R" value={sigs.avg_rr} />
          <StatCard label="Avg Return" value={`${sigs.avg_return}R`} />
          <StatCard label="Max Drawdown" value={`${sigs.max_drawdown}R`} accent="text-warning" />
        </div>
      )}

      {tab === "Feature Flags" && (
        <div className="space-y-2">
          {Object.entries(flags).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <span className="capitalize">{k.replace(/_/g, " ")}</span>
              <button onClick={() => saveFlags(k, !v)} data-testid={`admin-flag-${k}`} className={`h-6 w-11 rounded-full p-0.5 transition-colors ${v ? "bg-primary" : "bg-secondary"}`}>
                <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${v ? "translate-x-5" : ""}`} />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "Audit" && (
        <div className="rounded-2xl border border-border bg-card p-5">
          {logs.length === 0 ? <p className="text-sm text-muted-foreground">No audit logs yet.</p> : (
            <div className="space-y-2 text-sm">
              {logs.map((l, i) => <div key={i} className="flex justify-between border-b border-border py-2 last:border-0"><span>{l.action} → {l.role}</span><span className="text-muted-foreground">{l.actor}</span></div>)}
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
